import { theme } from "@xola/ui-kit";
import { chunk, inRange } from "lodash";
import { CSSProperties } from "react";
import { PreferenceKey, Timeslot } from "types";
import { findByKey } from "@/models/Preference";
import { DashboardColorPreference, DashboardCustomizationPreference } from "@/types/Dashboard";
import { getCollection } from "@/utils/getCollection";

// const bgColorOpacity = 0.4;
const bgColorLighteningOffset = 0.8;

type ColorProps = { style?: CSSProperties };

export const getListViewColor = (experienceId: ID, timeslot: Timeslot): ColorProps | undefined => {
    const preferences = getCollection<DashboardColorPreference>("preferences");
    if (!preferences) {
        return;
    }

    const preference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS);
    if (!preference) {
        return;
    }

    const { background } = preference.value;
    const experiences = preference.value.experiences ?? {};

    if (background === DashboardCustomizationPreference.AVAILABILITY) {
        const color = getColorForAvailability(timeslot);
        return {
            style: { backgroundColor: hexToRgba(color, 1, bgColorLighteningOffset), borderColor: hexToRgba(color) },
        };
    }

    if (background === DashboardCustomizationPreference.LISTING && experiences[experienceId]) {
        const color = experiences[experienceId];
        return {
            style: { backgroundColor: hexToRgba(color, 1, bgColorLighteningOffset), borderColor: hexToRgba(color) },
        };
    }
};

interface DashboardCalendarItemStyle {
    backgroundColor: string;
    borderLeft?: string;
    border?: string;
    color?: string;
}

export const getCalendarColors = (timeslot: Timeslot, experienceId?: ID, isEndOfMultiDayEvent?: boolean) => {
    const borderType = isEndOfMultiDayEvent ? "dotted" : "solid";

    const colorDanger = hexToRgba(theme.colors.danger.DEFAULT);
    const colorSuccess = hexToRgba(theme.colors.success.DEFAULT);

    const { open } = timeslot.capacity;
    const styles: DashboardCalendarItemStyle = {
        backgroundColor: hexToRgba(theme.colors.gray.lighter),
        borderLeft: open > 0 ? `3px ${borderType} ${colorSuccess}` : `3px ${borderType} ${colorDanger}`,
    };

    const preferences = getCollection<DashboardColorPreference>("preferences");
    const preference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS);
    if (!preference) {
        return styles;
    }

    const { background, sidebar } = preference.value;
    const experiences = preference.value.experiences ?? {};
    const hasExperience = experienceId && experiences[experienceId];

    if (background === DashboardCustomizationPreference.LISTING && hasExperience) {
        const bgColorHex = experiences[experienceId];
        styles.border = `1px ${borderType} ${hexToRgba(bgColorHex)}`;
        styles.backgroundColor = hexToRgba(bgColorHex, 1, bgColorLighteningOffset);
    }

    if (sidebar === DashboardCustomizationPreference.LISTING && hasExperience) {
        delete styles.border;
        delete styles.borderLeft;
        const color = experiences[experienceId];
        styles.borderLeft = `3px ${borderType} ${hexToRgba(color)}`;
    }

    const availabilityColor = getColorForAvailability(timeslot);
    if (sidebar === DashboardCustomizationPreference.AVAILABILITY) {
        delete styles.border;
        delete styles.borderLeft;
        styles.borderLeft = `3px solid ${hexToRgba(availabilityColor)}`;
    }

    if (background === DashboardCustomizationPreference.AVAILABILITY) {
        styles.backgroundColor = hexToRgba(availabilityColor, 1, bgColorLighteningOffset);
    }

    return styles;
};

const getColorForAvailability = (timeslot: Timeslot) => {
    const colorDanger = theme.colors.danger.DEFAULT;
    const colorSuccess = theme.colors.success.DEFAULT;
    const colorWarning = theme.colors.warning.DEFAULT;

    const { open, reserved, max } = timeslot.capacity;
    const reservedPercentage = (reserved / max) * 100;

    return open <= 0 ? colorDanger : inRange(reservedPercentage, 50, 100) ? colorWarning : colorSuccess;
};

export const hexToRgba = (hex: string, opacity = 1, offset = 1) => {
    if (hex.toLowerCase().includes("rgb")) {
        return hex;
    }

    const adjustedColor = offset < 1 ? lightenColor(hex, offset) : hex;
    const hexWithoutHash = adjustedColor.replace("#", "");
    const r = Number.parseInt(hexWithoutHash.slice(0, 2), 16);
    const g = Number.parseInt(hexWithoutHash.slice(2, 4), 16);
    const b = Number.parseInt(hexWithoutHash.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Lightens a HEX color by X% using Lodash
const lightenColor = (color: string, offset = 0.5): string => {
    const hex = color.replace("#", "");

    const rgb = chunk(hex.match(/.{2}/g)?.map((value) => Number.parseInt(value, 16)) ?? [], 1);
    const lighter = rgb.map(([channel]) => Math.round(channel + (255 - channel) * offset));

    return "#" + lighter.map((value) => value.toString(16).padStart(2, "0")).join("");
};
